import React, { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { siteStats } from "../../../data/siteStats";

const useCountUp = (end, duration = 2000, start = 0) => {
  const [count, setCount] = useState(start);
  const [isRunning, setIsRunning] = useState(false);

  const startCount = () => {
    if (isRunning) return;
    setIsRunning(true);
    const startTime = performance.now();
    const step = (currentTime) => {
      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / duration, 1);
      // Ease-out cubic
      const eased = 1 - Math.pow(1 - progress, 3);
      setCount(Math.floor(eased * (end - start) + start));
      if (progress < 1) {
        requestAnimationFrame(step);
      } else {
        setCount(end);
      }
    };
    requestAnimationFrame(step);
  };

  return { count, startCount };
};

const StatItem = ({ value, suffix, label }) => {
  const { count, startCount } = useCountUp(value, 2200);
  const ref = useRef(null);
  const triggered = useRef(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !triggered.current) {
          triggered.current = true;
          startCount();
        }
      },
      { threshold: 0.3 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div ref={ref} className="flex flex-col items-center justify-center text-center py-4 md:py-0">
      <div className="text-3xl md:text-5xl lg:text-6xl font-black text-primary mb-3 tracking-tighter drop-shadow-sm tabular-nums">
        {count}{suffix}
      </div>
      <div className="text-[13px] tracking-widest uppercase font-bold text-gray-400">
        {label}
      </div>
    </div>
  );
};

const ArdrendStatistics = () => {
  const { t } = useTranslation();
  return (
    <section className="bg-gradient-to-r from-[#1a2333] via-[#0b0f19] to-[#05070c] py-16 border-y border-white/5 relative overflow-hidden">
       {/* Background glows */}
       <div className="absolute top-0 left-1/4 w-[400px] h-[400px] bg-primary/10 rounded-full mix-blend-screen filter blur-[120px] opacity-40"></div>
       <div className="absolute bottom-0 right-1/4 w-[300px] h-[300px] bg-white/5 rounded-full mix-blend-screen filter blur-[100px] opacity-20"></div>

       <div className="max-w-[1560px] mx-auto px-6 relative z-10">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-12 divide-y md:divide-y-0 md:divide-x divide-white/10">
            {siteStats.map(({ key, value, suffix }) => (
              <StatItem key={key} value={value} suffix={suffix} label={t(`home.stats.${key}`)} />
            ))}
          </div>
       </div>
    </section>
  );
};

export default ArdrendStatistics;
